/**
 * upgradePlansModal.tsx
 */
import BackgroundMask from '../../atoms/mask/backgroundMask';
import Badge from '../../atoms/images/badge';
import ButtonsContainer from '@components/atoms/container/modalButtons';
import Channel from '@shared/classes/channel';
import Focus from '@shared/types/enums/focus';
import List from '../list/list';
import ModalButton from '@components/elements/buttons/ui/modalButton';
import ModalContainer from '../../atoms/container/modalContainer';
import Plan from '@shared/classes/plan';
import TranslationManager from '@shared/assets/locale/translationsManager';
import UpgradeButton from '../../elements/buttons/ui/upgradeButton';
import UpgradePlanModalContainer from '../../atoms/container/upgradePlanModalContainer';
import UpgradePlanModalSubtitle from '../../atoms/text/upgradePlanModalSubtitle';
import UpgradePlanModalTitle from '../../atoms/text/upgradePlanModalTitle';
import addPlanRequest from '@shared/api/addPlan/request';
import useCloseModalKeyboard from '@shared/components/utils/hooks/useCloseModalKeyboard';
import { clearModal, modalSelector } from '@shared/store/slices/modal.slice';
import { popFocus, pushFocus } from '@shared/store/slices/focus.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useState } from 'react';

/**
 * Displays the upgrade plans modal.
 */
export default function UpgradePlanModal() {
  const dispatch = useAppDispatch();
  const { params } = useAppSelector(modalSelector);
  const [accepted, setAccepted] = useState(params?.accepted || false);

  useCloseModalKeyboard();

  const buttons = [
    {
      title: TranslationManager.get('MODAL.UPGRADE_PLANS.BUTTONS.WANT_IT'),
      action: () => {
        addPlanRequest({
          channelCode: (params as { channel: Channel })?.channel?.code,
        });
        setAccepted(true);
      },
      Component: UpgradeButton,
    },
    {
      title: TranslationManager.get('MODAL.UPGRADE_PLANS.BUTTONS.CANCEL'),
      releaseAction: () => {
        dispatch(popFocus());
        dispatch(clearModal());
      },
      Component: ModalButton,
    },
  ];

  useEffect(() => {
    dispatch(pushFocus(Focus.MODAL_BUTTONS));
  }, []);

  return (
    <ModalContainer>
      <BackgroundMask opacity={0.9}>
        <UpgradePlanModalContainer top={accepted ? 370 : 270}>
          {!accepted ? (
            <>
              <Badge
                height={130}
                width={130}
                src={(params as { plan: Plan })?.plan?.badge}
              />
              <UpgradePlanModalTitle>
                {TranslationManager.get('MODAL.UPGRADE_PLANS.TITLE')}
                {(params as { plan: Plan })?.plan?.name}.
              </UpgradePlanModalTitle>
              <UpgradePlanModalSubtitle>
                {TranslationManager.get('MODAL.UPGRADE_PLANS.SUBTITLE')}
              </UpgradePlanModalSubtitle>
              <ButtonsContainer>
                <List focus={Focus.MODAL_BUTTONS}>
                  {buttons.map((item, index) => {
                    const Button = item.Component;
                    return (
                      <Button
                        action={item.action}
                        releaseAction={item.releaseAction}
                        index={index}
                        text={item.title}
                        key={index}
                      />
                    );
                  })}
                </List>
              </ButtonsContainer>
            </>
          ) : (
            <>
              <UpgradePlanModalTitle>
                {TranslationManager.get('MODAL.UPGRADE_PLANS.REQUEST')}
              </UpgradePlanModalTitle>
              {/* Remounted list: the key-up of the "I want it" press doesn't reach it */}
              <List focus={Focus.MODAL_BUTTONS}>
                {[
                  <ModalButton
                    releaseAction={() => {
                      dispatch(popFocus());
                      dispatch(clearModal());
                    }}
                    index={0}
                    text='OK'
                    key={0}
                  />,
                ]}
              </List>
            </>
          )}
        </UpgradePlanModalContainer>
      </BackgroundMask>
    </ModalContainer>
  );
}
